import { cloneElement, type ComponentProps, type ReactElement, type ReactNode } from 'react';
import { Box, Icon, Menu, Text, TextButton } from '@zillow/constellation-10';
import { IconChevronLeftFilled } from '@zillow/constellation-icons';
import { useOverflowCollapse } from '../../hooks/useOverflowCollapse';

/**
 * Breadcrumb — the ZRM breadcrumb trail for page headers.
 *
 * Renders inside Constellation's `Page.Breadcrumb` slot (which only provides spacing) and
 * supplies the `<nav aria-label="Breadcrumb">`, the slash-separated trail of ancestor links,
 * and the bold current page. Replaces the single "‹ Back" `TextButton` most apps render today.
 *
 * **Anatomy**
 * - Ancestor crumb: `TextButton tone="subtle" textStyle="body-sm"`, optional icon, rendered as
 *   the consumer's own link element via `asChild` (react-router `Link`, Next `Link`, or `<a>`).
 *   The icon is part of the link, not a decorative sibling.
 * - Separator: `/` in `text.subtle`, hidden from screen readers.
 * - Current crumb: `body-sm-bold` in `text.neutral`, `aria-current="page"`, never a link.
 * - Below the `md` breakpoint (Constellation's mobile → tablet boundary) the trail is replaced
 *   by one back link to the parent crumb with a left chevron, matching the legacy back button.
 * - When the full trail would not fit on one line (`collapse="auto"`, the default) the middle
 *   crumbs collapse into a `...` button that opens a Menu of those crumbs as links.
 *
 * **DO**
 * - Pass the ancestors root-first in `items` and the current page separately in `current`.
 * - Pass a bare link element without children: `{ label: 'Properties', link: <Link to="/properties" /> }`.
 *   The component sets the label as the link's children.
 * - Keep labels short; the current label is the page title in most cases.
 * - Use the ZRM tab and nav names for section crumbs ("Properties", "Payments", "Maintenance").
 * - Write a property crumb as the full one-line address from `formatAddress` (Address format pattern):
 *   `123 Main St Unit 5B, Seattle, WA 98101`. Pass the stored street, unit, city, state and ZIP; do not
 *   build the string yourself.
 *
 * **DO NOT**
 * - Do not wrap `link` in `TextButton` or add your own icon inside it — the component styles it.
 * - Do not make the current page a link.
 * - Do not render it outside `Page.Breadcrumb`; the slot owns the page-level margins and padding.
 *
 * @example
 * <Page.Breadcrumb>
 *   <Breadcrumb
 *     items={[
 *       { label: 'Account', icon: <IconHomesFilled />, link: <Link to="/" /> },
 *       { label: 'Properties', icon: <IconBuildingsFilled />, link: <Link to="/properties" /> },
 *     ]}
 *     current={{ label: '123 Main St Unit 5B, Seattle, WA 98101', icon: <IconBuildingsFilled /> }}
 *   />
 * </Page.Breadcrumb>
 */
export type BreadcrumbItem = {
  /** Visible text of the crumb. Also used as the link's children. */
  label: string;
  /** Optional Constellation icon element, e.g. `<IconHomesFilled />`. Rendered inside the link. */
  icon?: ReactNode;
  /**
   * The navigation element for this crumb, without children. Rendered through `TextButton asChild`
   * so it keeps its own routing behavior: `<Link to="/x" />` (react-router), `<Link href="/x" />`
   * (Next.js), or `<a href="/x" />`.
   */
  link: ReactElement;
};

export type BreadcrumbCurrent = {
  /** Visible text of the current page. */
  label: string;
  /** Optional Constellation icon element rendered before the label. */
  icon?: ReactNode;
};

export type BreadcrumbProps = {
  /** Ancestor crumbs, root first. Does not include the current page. */
  items: Array<BreadcrumbItem>;
  /** The current page, rendered bold with `aria-current="page"`. */
  current: BreadcrumbCurrent;
  /**
   * `auto` collapses the middle crumbs into `...` when the trail overflows one line.
   * `always` collapses whenever there are at least two ancestors. `never` shows the full trail.
   * @default auto
   */
  collapse?: 'auto' | 'always' | 'never';
  /** Accessible name of the `<nav>`. @default Breadcrumb */
  'aria-label'?: string;
  /** Styles applied to the `<nav>`. */
  css?: ComponentProps<typeof Box>['css'];
};

const SHOW_FULL_BREADCRUMB_LABEL = 'Show full breadcrumb';

function CrumbLink({ item, icon }: { item: BreadcrumbItem; icon?: ReactNode }): ReactElement {
  return (
    <TextButton asChild tone="subtle" textStyle="body-sm" icon={icon}>
      {cloneElement(item.link, undefined, item.label)}
    </TextButton>
  );
}

function Separator(): ReactElement {
  return (
    <Text textStyle="body-sm" color="text.subtle" aria-hidden="true">
      /
    </Text>
  );
}

function Crumb({ item }: { item: BreadcrumbItem }): ReactElement {
  return (
    <Box css={{ display: 'flex', alignItems: 'center', gap: 'tighter' }}>
      <CrumbLink item={item} icon={item.icon} />
      <Separator />
    </Box>
  );
}

function CurrentCrumb({ current }: { current: BreadcrumbCurrent }): ReactElement {
  return (
    <Text
      asChild
      textStyle="body-sm-bold"
      color="text.neutral"
      css={{ display: 'inline-flex', alignItems: 'center', gap: 'tightest' }}
    >
      <span aria-current="page">
        {current.icon ? <Icon render={current.icon} /> : null}
        {current.label}
      </span>
    </Text>
  );
}

function FullTrail({ items, current }: Pick<BreadcrumbProps, 'items' | 'current'>): ReactElement {
  return (
    <>
      {items.map((item, index) => (
        <Crumb key={`${item.label}-${index}`} item={item} />
      ))}
      <CurrentCrumb current={current} />
    </>
  );
}

function CollapsedTrail({ items, current }: Pick<BreadcrumbProps, 'items' | 'current'>): ReactElement {
  const [first, ...hidden] = items;

  return (
    <>
      <Crumb item={first} />
      <Box css={{ display: 'flex', alignItems: 'center', gap: 'tighter' }}>
        <Menu
          placement="bottom-start"
          trigger={
            <TextButton tone="subtle" textStyle="body-sm" aria-label={SHOW_FULL_BREADCRUMB_LABEL}>
              ...
            </TextButton>
          }
          content={hidden.map((item, index) => (
            <Menu.Item key={`${item.label}-${index}`} asChild>
              {cloneElement(
                item.link,
                undefined,
                <>
                  {item.icon ? <Menu.ItemIcon render={item.icon} /> : null}
                  <Menu.ItemLabel>{item.label}</Menu.ItemLabel>
                </>,
              )}
            </Menu.Item>
          ))}
        />
        <Separator />
      </Box>
      <CurrentCrumb current={current} />
    </>
  );
}

export function Breadcrumb({
  items,
  current,
  collapse = 'auto',
  'aria-label': ariaLabel = 'Breadcrumb',
  css,
}: BreadcrumbProps): ReactElement {
  const canCollapse = items.length >= 2;
  const { containerRef, contentRef, isOverflowing } = useOverflowCollapse<HTMLDivElement, HTMLDivElement>(
    canCollapse && collapse === 'auto',
  );
  const isCollapsed = canCollapse && (collapse === 'always' || (collapse === 'auto' && isOverflowing));
  const parent = items.at(-1);

  return (
    <Box asChild css={css}>
      <nav aria-label={ariaLabel}>
        <Box
          ref={containerRef}
          css={{
            position: 'relative',
            display: { base: 'none', md: 'flex' },
            alignItems: 'center',
            flexWrap: 'wrap',
            columnGap: 'tighter',
            rowGap: 'tighter',
          }}
        >
          {isCollapsed ? (
            <CollapsedTrail items={items} current={current} />
          ) : (
            <FullTrail items={items} current={current} />
          )}
          {collapse === 'auto' ? (
            <Box
              ref={contentRef}
              aria-hidden="true"
              css={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: 'max-content',
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'nowrap',
                gap: 'tighter',
                whiteSpace: 'nowrap',
                visibility: 'hidden',
                pointerEvents: 'none',
              }}
            >
              <FullTrail items={items} current={current} />
            </Box>
          ) : null}
        </Box>
        {parent ? (
          <TextButton
            asChild
            tone="subtle"
            textStyle="body-sm"
            icon={<IconChevronLeftFilled />}
            css={{ display: { base: 'inline-flex', md: 'none' } }}
          >
            {cloneElement(parent.link, undefined, parent.label)}
          </TextButton>
        ) : null}
      </nav>
    </Box>
  );
}
